import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusPill from "@/components/StatusPill";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";

export default function Reconciliation() {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [data, setData] = useState(null);
    const [matchResult, setMatchResult] = useState(null);
    const [matching, setMatching] = useState(false);

    useEffect(() => {
        api.get("/reconciliation/summary", { params: { month } }).then((r) => setData(r.data));
        setMatchResult(null);
    }, [month]);

    const runAutoMatch = async (apply) => {
        setMatching(true);
        try {
            const { data: res } = await api.post("/reconciliation/auto-match", { month, apply });
            setMatchResult(res);
            const label = apply
                ? `Applied ${res.applied_updates} match${res.applied_updates === 1 ? "" : "es"}`
                : `Previewed ${res.matched}/${res.expenses_scanned} matched`;
            toast.success(label);
            if (apply) {
                const { data: refreshed } = await api.get("/reconciliation/summary", { params: { month } });
                setData(refreshed);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auto-match failed");
        } finally {
            setMatching(false);
        }
    };

    const rows = data?.rows || [];
    const totals = rows.reduce(
        (acc, r) => {
            acc.exp += Number(r.expenses_total || 0);
            acc.stmt += Number(r.statement_purchases || 0);
            acc.paid += Number(r.payments_total || 0);
            acc.due += Number(r.total_amount_due || 0);
            return acc;
        },
        { exp: 0, stmt: 0, paid: 0, due: 0 },
    );

    return (
        <div data-testid="reconciliation-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Reconciliation</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Match &amp; close.</h1>
                    <p className="text-sm text-stone-500 mt-3">Compare posted expenses vs uploaded statements vs payments made.</p>
                </div>
                <div>
                    <div className="eyebrow mb-1">Month</div>
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="recon-month" />
                </div>
            </div>

            {/* Auto-match toolbar */}
            <div className="mb-5 flex flex-wrap items-center gap-3 p-4 rounded-lg border border-stone-200 bg-white">
                <div className="flex items-center gap-2 text-sm">
                    <Wand2 size={14} className="text-stone-500" />
                    <span className="font-medium text-stone-900">Auto-match transactions</span>
                    <span className="text-stone-500 text-xs">Pair posted expenses with statement transactions using amount + date + merchant.</span>
                </div>
                <div className="ml-auto flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => runAutoMatch(false)}
                        disabled={matching}
                        data-testid="auto-match-preview"
                    >
                        {matching ? "Scanning…" : "Preview matches"}
                    </Button>
                    <Button
                        size="sm"
                        className="bg-stone-900 hover:bg-stone-800 text-white"
                        onClick={() => runAutoMatch(true)}
                        disabled={matching}
                        data-testid="auto-match-apply"
                    >
                        {matching ? "Applying…" : "Auto-match & save"}
                    </Button>
                </div>
                {matchResult && (
                    <div className="w-full pt-3 border-t border-stone-100 text-xs text-stone-600 flex flex-wrap gap-4" data-testid="auto-match-result">
                        <span><b className="text-stone-900 tabular-nums">{matchResult.expenses_scanned}</b> expenses scanned</span>
                        <span><b className="text-emerald-700 tabular-nums">{matchResult.exact_matches}</b> exact</span>
                        <span><b className="text-blue-700 tabular-nums">{matchResult.strong_matches}</b> strong</span>
                        <span><b className="text-amber-700 tabular-nums">{matchResult.possible_matches}</b> possible</span>
                        <span><b className="text-red-700 tabular-nums">{matchResult.unmatched}</b> unmatched</span>
                        {matchResult.applied_updates > 0 && (
                            <span className="text-emerald-700"><b className="tabular-nums">{matchResult.applied_updates}</b> saved to statement</span>
                        )}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="kpi-card"><div className="eyebrow">Expenses posted</div><div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(totals.exp)}</div></div>
                <div className="kpi-card"><div className="eyebrow">Statement purchases</div><div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(totals.stmt)}</div></div>
                <div className="kpi-card"><div className="eyebrow">Payments made</div><div className="font-display text-2xl mt-2 tabular-nums text-emerald-700">{fmtMoney(totals.paid)}</div></div>
                <div className="kpi-card"><div className="eyebrow">Total due</div><div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(totals.due)}</div></div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Card</th>
                                <th className="text-right">Expenses Posted</th>
                                <th className="text-right">Statement Purchases</th>
                                <th className="text-right">Difference</th>
                                <th className="text-right">Payments</th>
                                <th className="text-right">Late Fee</th>
                                <th className="text-right">Interest</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.card_id} data-testid={`recon-row-${r.card_id}`}>
                                    <td>{r.bank_name} · {r.card_name} · •••• {r.last_4}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(r.expenses_total)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(r.statement_purchases)}</td>
                                    <td className={`text-right tabular-nums font-medium ${Math.abs(r.difference) > 1 ? "text-amber-700" : "text-stone-500"}`}>{fmtMoney(r.difference)}</td>
                                    <td className="text-right tabular-nums text-emerald-700">{fmtMoney(r.payments_total)}</td>
                                    <td className="text-right tabular-nums text-red-700">{r.late_fee ? fmtMoney(r.late_fee) : "—"}</td>
                                    <td className="text-right tabular-nums text-red-700">{r.interest ? fmtMoney(r.interest) : "—"}</td>
                                    <td>
                                        <StatusPill status={r.status === "Reconciled" ? "Paid" : r.status === "Statement Missing" ? "Overdue" : r.status === "Difference Found" ? "Due Soon" : "Not Due"} />
                                        <span className="ml-2 text-[11px] text-stone-500">{r.status}</span>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={8} className="text-center text-stone-500 py-14">No cards yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
